import { useMemo } from "react";

// A circular "ring" gauge that visualizes distance and off-road percentage.
// When mode='time' the ring displays TIME as the big central value and km
// as the small secondary line. When mode='distance' (default) it's the
// opposite. This mirrors whichever slider the rider is actively adjusting.
export default function RingConfigurator({
  distance,
  maxDistance = 200,
  offroad,
  vehicle,
  duration,
  mode = "distance",
  timeH,
  loading = false,
}) {
  const R = 78;
  const C = 2 * Math.PI * R;
  const distPct = Math.min(1, distance / maxDistance);
  const offroadPct = offroad / 100;

  const distDash = useMemo(() => `${C * distPct} ${C}`, [C, distPct]);
  const offDash = useMemo(() => `${C * offroadPct} ${C}`, [C, offroadPct]);

  // Compute the primary (big) and secondary (small) display values based on
  // which slider the user is actively driving.
  const isTimeMode = mode === "time";
  const durHours = Math.floor(duration / 60);
  const durMins = duration % 60;
  const primaryValue = isTimeMode
    ? (timeH != null ? formatTime(timeH) : `${durHours}h ${durMins}m`)
    : Math.round(distance);
  const primaryLabel = isTimeMode ? "HOURS" : "KILOMETERS";
  const secondaryValue = isTimeMode
    ? `~ ${Math.round(distance)} KM`
    : `~ ${durHours}h ${durMins}m`;

  // Dynamic glow behind the ring: electric blue for MOTO, amber for E-BIKE.
  // Two-layer soft glow — a punchy inner core (~28% peak) surrounded by a
  // wider softer bloom (~10%) that decays into the dark background. Creates
  // a real "elegant halo" effect on the Dark Petrol base without turning
  // plasticky. Transitions on vehicle change.
  const isMoto = vehicle === "moto";
  const coreRgba = isMoto ? "59, 130, 246" : "245, 158, 11"; // electric blue / amber
  const glowLayers = `
    radial-gradient(circle at center, rgba(${coreRgba}, 0.32) 0%, rgba(${coreRgba}, 0.16) 30%, rgba(${coreRgba}, 0) 62%),
    radial-gradient(circle at center, rgba(${coreRgba}, 0.14) 0%, rgba(${coreRgba}, 0.05) 55%, rgba(${coreRgba}, 0) 85%)
  `;

  return (
    <div className="relative w-[220px] h-[220px] flex items-center justify-center">
      <div
        aria-hidden="true"
        className={`absolute pointer-events-none rounded-full${loading ? " brema-glow-pulse" : ""}`}
        style={{
          inset: "-24px",
          background: glowLayers,
          filter: "blur(14px)",
          transition: "background 320ms ease",
        }}
        data-testid="ring-glow"
      />
      <svg viewBox="0 0 200 200" className="w-full h-full -rotate-90 relative">
        {/* Outer track */}
        <circle
          cx="100"
          cy="100"
          r={R}
          stroke="#1e1e1e"
          strokeWidth="10"
          fill="none"
        />
        {/* Distance arc (yellow) */}
        <circle
          cx="100"
          cy="100"
          r={R}
          stroke="#FFD400"
          strokeWidth="10"
          strokeLinecap="butt"
          fill="none"
          strokeDasharray={distDash}
          style={{ transition: "stroke-dasharray 400ms ease" }}
        />
        {/* Inner track */}
        <circle
          cx="100"
          cy="100"
          r={R - 16}
          stroke="#1e1e1e"
          strokeWidth="4"
          fill="none"
        />
        {/* Off-road arc (red) */}
        <circle
          cx="100"
          cy="100"
          r={R - 16}
          stroke="#C1272D"
          strokeWidth="4"
          fill="none"
          strokeDasharray={offDash}
          style={{ transition: "stroke-dasharray 400ms ease" }}
        />
        {/* tick marks */}
        {Array.from({ length: 24 }).map((_, i) => {
          const a = (i * Math.PI * 2) / 24;
          const x1 = 100 + Math.cos(a) * (R + 10);
          const y1 = 100 + Math.sin(a) * (R + 10);
          const x2 = 100 + Math.cos(a) * (R + 14);
          const y2 = 100 + Math.sin(a) * (R + 14);
          return (
            <line
              key={i}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={i % 6 === 0 ? "#FFD400" : "#3f3f46"}
              strokeWidth={i % 6 === 0 ? 1.5 : 1}
            />
          );
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center" data-testid="ring-center">
        <div className="font-mono text-[9px] text-zinc-200 uppercase tracking-[0.35em]" data-testid="ring-vehicle">
          {vehicle === "moto" ? "Moto" : "E-Bike"}
        </div>
        <div className="font-display font-black text-white text-5xl leading-none mt-1" data-testid="ring-primary">
          {primaryValue}
        </div>
        <div className="font-mono text-[10px] text-brema-yellow tracking-[0.3em] mt-1">
          {primaryLabel}
        </div>
        <div className="font-mono text-[10px] text-zinc-200 tracking-[0.25em] mt-3" data-testid="ring-secondary">
          {secondaryValue}
        </div>
        <div className="font-mono text-[10px] text-brema-red tracking-[0.3em] mt-1" data-testid="ring-offroad">
          {offroad}% OFF-ROAD
        </div>
      </div>
    </div>
  );
}

function formatTime(h) {
  const hours = Math.floor(h);
  const mins = Math.round((h - hours) * 60);
  if (mins === 0) return `${hours}h`;
  return `${hours}h${String(mins).padStart(2, "0")}`;
}
